import { Accordion, Box, Button, Link, Text } from '@clariness/clarikit-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { themes } from '@clariness/clarikit-tokens';
import { captureEventV2 } from 'utils/events/captureEvent';
import { Events } from '@clariness/tracking-event-runtime';
import { FAQIcon } from './FAQIcon';
import { ThumbsDownIcon, ThumbsUpIcon } from '@clariness/clarikit-icons';

const { space } = themes.light;

const questionsAndAnswers = [
  {
    questionKey: 'is.faq.clinlife_patient_recruitment.question',
    question: 'How does ClinLife patient recruitment work?',
    answerKey: 'is.faq.clinlife_patient_recruitment.answer',
    answer:
      '<Text>ClinLife provides an essential link between research sites and people seeking clinical research studies. It streamlines patient recruitment by matching patients with relevant clinical trials worldwide. Patients search by medical condition, complete a short eligibility screening, and if qualified, their details are securely shared with the research site for follow-up. {linebreak}{linebreak}{linebreak}The patient recruitment journey consists of several steps:  {linebreak}<ol><li>Research - study population identification of potential patients from Clariness’ database and publicly available information</li><li>Reach -  Targeted ad campaigns across 40+ digital channels, patient communities, apps & registries</li>  <li>Inform - We educate and inform patients on suitable clinical trials</li>  <li>Pre-Screening – We develop pre-screener questions to match the right patients to your study</li><li>Site Selection - Patients select a trial site that is geographically nearby and best suits them</li><li>Registration - Patients sign up on our GDPR compliant platform to register their interest</li><li>Site referrals & randomizations - Pre-screened and qualified patient referrals are sent to sites via the ClinLife® portal.</li></ol></Text>',
  },
  {
    questionKey: 'is.faq.get_patient_referrals.question',
    question: 'How do I get patient referrals?',
    answerKey: 'is.faq.get_patient_referrals.answer',
    answer:
      "<Text>Once your site is onboarded, no further action is needed on your end. We will begin advertising your site as soon as regulatory approvals are in place, and you’ll start seeing patient referrals when you log in. To receive more patient referrals, ensure patient status updates are timely, and that you have communicated the site capacity to the Site Partnership Manager. If you're unsure whether the marketing campaign is live, please reach out to our team.</Text>",
  },
  {
    questionKey: 'is.faq.update_patient_status_continously.question',
    question: 'Why do I need to update the patient status continuously?',
    answerKey: 'is.faq.update_patient_status_continously.answer',
    answer:
      '<Text>Maintaining updated patient statuses ensures up-to-date data on trial progress and enhances site efficiency. It supports patient recruitment tracking, improves the patient experience, and optimizes resource allocation. The Clariness team is also able to help contact patients who are difficult to reach. If you are unsure which status to use for a patient, please refer to the guide found here.</Text>',
  },
  {
    questionKey: 'is.faq.patient_statuses.question',
    question: 'What are the patient statuses, and when do they apply?',
    answerKey: 'is.faq.patient_statuses.answer',
    answer:
      '<Text>Every new patient arriving at the site is labelled with the status “New Referral”. The next default status is “Appointment Scheduled”, which indicates the patient has a site appointment scheduled and will soon undergo the necessary screening.{linebreak}If you encounter difficulties reaching the patient, you can choose one of the following statuses, depending on the situation: <ul><li><strong>“Attempting Contact”</strong> – Use this status when you have tried to reach the patient but have been unsuccessful. After selecting this status, you can specify a more detailed explanation.</li><li><strong>“Contacted”</strong> – Use this status when you’ve reached the patient, but scheduling an appointment was not possible. You can add further details to clarify the situation.</li></ul>If you successfully schedule an appointment, update the patient’s status to <strong>“Appointment Scheduled.”</strong>{linebreak}{linebreak}The next key status is <strong>“Consented.”</strong> If the patient attends the appointment and provides consent to participate in the study, you can change their status to “Consented.” If the patient is interested but cannot provide consent immediately, select <strong>“Waiting for Information”</strong> and choose a subcategory that best describes their situation. {linebreak}{linebreak}Please note that a “Consented” patient is not yet part of the study. The final status is <strong>“Randomized.”</strong>{linebreak}{linebreak}At any point, if a patient is no longer able or willing to participate in the study, you can mark them as <strong>“Dropped.”</strong> You’ll find this option at the bottom of the status change window. Be sure to select a subcategory that explains the reason for dropping the patient.</Text>',
  },
  {
    questionKey: 'is.faq.patient_with_XX_icon.question',
    question: 'Why are some patients marked at the top with the XX icon?',
    answerKey: 'is.faq.patient_with_XX_icon.answer',
    answer:
      '<Text>This icon indicates which patients were successfully recontacted and reached by the Clariness team after the patient was referred to your site. These patients are still interested in participating in the study and hence should be contacted and processed as soon as possible.</Text>',
  },
  {
    questionKey: 'is.faq.find_patients_assigned_to_same_study.question',
    question: 'How to find patients assigned to the same study? ',
    answerKey: 'is.faq.find_patients_assigned_to_same_study.answer',
    answer:
      '<Text>In the patient list, you can view all patients assigned to your site. If you want to see patients for a specific study, click the filter icon in the top right corner of the patient list. This will open a list of available filters for you to choose from.{linebreak}{linebreak}To filter by study, select the “Study” filter, choose the study you’re interested in, and click the “Apply” button. The patient list will then only display the patients assigned to the selected study.</Text>',
  },
  {
    questionKey: 'is.faq.site_escalated.question',
    question: 'My site was escalated - what does this mean?',
    answerKey: 'is.faq.site_escalated.answer',
    answer:
      '<Text>If your site was escalated, it means the Clariness team has identified an issue that requires further attention. This could relate to delays in patient status updates or other site-specific concerns. Escalation ensures that the right support is provided to help resolve the issue efficiently. The site escalation process and criteria are agreed upon with the Sponsor for each project.</Text>',
  },
  {
    questionKey: 'is.faq.no_patients_referred.question',
    question: 'Why are there no patients referred to my site?',
    answerKey: 'is.faq.no_patients_referred.answer',
    answer:
      '<Text>There could be several reasons why your site hasn’t received patient referrals, including patient availability in your region or pending site activation steps. If you believe there’s an issue, please reach out to our team for further support.</Text>',
  },
];

export const FAQ = () => {
  const [openQuestions, setOpenQuestions] = useState(
    questionsAndAnswers.map(() => false)
  );

  const [wasHelpful, setWasHelpful] = useState<string>('');

  const handleQuestionClicked = (value: boolean, index: number): void => {
    if (value) {
      captureEventV2(
        Events.InvestigatorService.InvestigatorOpensFAQQuestion({
          QuestionName: questionsAndAnswers[index].question,
        })
      );
    }
    setWasHelpful('');

    setOpenQuestions(
      openQuestions.map((q, i) => {
        if (i === index) {
          return value;
        }
        return false;
      })
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      padding="112px 201px 385px 200p"
      gap={space['space-24']}
      width="75%"
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={space['space-24']}
        justifyContent="flex-start"
      >
        <FAQIcon />
        <Text as="h6" variant="headingMd">
          <FormattedMessage id="is.help_center.faq" defaultMessage="FAQ" />
        </Text>
      </Box>
      <Text as="span" variant="bodyMd">
        <FormattedMessage
          id="is.faq.description"
          defaultMessage="Find answers to common questions here. Need more help? Contact our Team."
        />
      </Text>
      <Box display="flex" flexDirection="column">
        {questionsAndAnswers.map((qa, i) => (
          <Box key={qa.question} display="flex" flexDirection="column">
            <Accordion
              title={
                <FormattedMessage
                  id={qa.questionKey}
                  defaultMessage={qa.question}
                />
              }
              content={
                <Box display="flex" flexDirection="column">
                  <FormattedMessage
                    id={qa.answerKey}
                    defaultMessage={qa.answer}
                    values={{
                      Text: (...chunks) => (
                        <Text as="span" variant="bodyMd">
                          {chunks}
                        </Text>
                      ),
                      linebreak: <br />,
                      ol: (...chunks) => <ol>{chunks}</ol>,
                      li: (...chunks) => <li>{chunks}</li>,
                      ul: (...chunks) => <ul>{chunks}</ul>,
                      strong: (...chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                  <br />
                  <br />
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    padding={`${space['space-16']} ${space['space-24']}`}
                    gap={space['space-16']}
                    alignItems="center"
                  >
                    <Text as="span" variant="bodyMd">
                      <FormattedMessage
                        id="is.faq.was_this_helpful"
                        defaultMessage="Was this helpful?"
                      />
                    </Text>
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="center"
                      gap={space['space-16']}
                    >
                      <Button
                        variant={wasHelpful === 'yes' ? 'primary' : 'secondary'}
                        iconLeft={ThumbsUpIcon}
                        size="s"
                        onClick={() => {
                          setWasHelpful('yes');
                          captureEventV2(
                            Events.InvestigatorService.InvestigatorGaveFAQFeedback(
                              {
                                QuestionName: qa.question,
                                WasHelpful: 'yes',
                              }
                            )
                          );
                        }}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={wasHelpful === 'no' ? 'primary' : 'secondary'}
                        iconLeft={ThumbsDownIcon}
                        size="s"
                        onClick={() => {
                          setWasHelpful('no');
                          captureEventV2(
                            Events.InvestigatorService.InvestigatorGaveFAQFeedback(
                              {
                                QuestionName: qa.question,
                                WasHelpful: 'no',
                              }
                            )
                          );
                        }}
                      >
                        No
                      </Button>
                    </Box>
                    {wasHelpful === 'yes' && (
                      <Text as="span" variant="bodyMd">
                        <FormattedMessage
                          id="is.faq.thank_you_for_feedback"
                          defaultMessage="Thank you for your feedback!"
                        />
                      </Text>
                    )}
                    {wasHelpful === 'no' && (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column"
                      >
                        <Text as="span" variant="bodyMd">
                          <FormattedMessage
                            id="is.faq.further_assistance"
                            defaultMessage="For further assistance contact our"
                          />
                        </Text>
                        <Link
                          url="mailto:info@clinlife.com"
                          color="color-green-primary"
                          size="m"
                        >
                          Clinlife Support
                        </Link>
                      </Box>
                    )}
                  </Box>
                </Box>
              }
              open={openQuestions[i]}
              handleAccordionClick={(value) => handleQuestionClicked(value, i)}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
